'use client';
import {
  CarInsuranceContext,
  CarInsuranceContextState,
  Driver,
  Prices,
  Vehicle,
} from '@hyphengroup/cms-components/components/CarInsurance/context';
import { getVehicleDomainService } from 'car-insurance/api/client/clientDomainServices';
import { moneymaxClientApiService } from 'car-insurance/api/moneymax/client';
import {
  mapVehicleMakers,
  mapVehicleModels,
  mapVehicleTrims,
} from 'car-insurance/components/PolicySearch/utils';
import { config, DefaultContextState } from 'car-insurance/constants';
import {
  Brand,
  SortType,
  VehicleModel,
  VehicleTrim,
} from 'car-insurance/types';
import { getDateOfBirth } from 'car-insurance/utils/getDateOfBith';
import {
  ReactNode,
  JSX,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import {
  getCarInsuranceLayoutId,
  SendLeadToZoho,
} from '$/app/[site]/[locale]/(application)/apply/[productId]/components/StickyQuickApply/utils.server';
import { useClientContext } from '$/modules/context/client';

export { ContactMethod } from '@hyphengroup/cms-components/types/car-insurance/ContactMethod';
export * from '@hyphengroup/cms-components/components/CarInsurance/context';

type Field<T> = Partial<{
  [key in keyof T]: T[key];
}>;

export function CarInsuranceContextProvider(props: {
  children: ReactNode;
  vehicleMakers: string[] | { id: string; name: string }[];
  vehicleModels: VehicleModel[];
  trims?: VehicleTrim[];
  initialState: DefaultContextState;
  useBolttechData?: boolean;
}): JSX.Element {
  const { brand: marketBrand } = useClientContext();
  const brand = marketBrand as Brand;
  const vehicleClient = getVehicleDomainService(marketBrand);
  const fairMarketClient = moneymaxClientApiService;

  const isMoneyMax = marketBrand === 'moneymax';
  const isSingSaver = marketBrand === 'singsaver';
  const useBolttechData = props?.useBolttechData || false;
  const initialState = props.initialState;

  // Skip only what the server preloaded: models need a make, variants need make, model and year.
  // Otherwise the first pick must fetch (CMS-2047). Development skips two runs: strict mode.
  const skipRuns = process.env.NODE_ENV === 'development' ? 2 : 1;
  const skipInitialFetchModelEffect = useRef(
    initialState.vehicle.makeName ? skipRuns : 0,
  );
  const skipInitialFetchTrimEffect = useRef(
    initialState.vehicle.makeName &&
      initialState.vehicle.modelCode &&
      initialState.vehicle.manufactureYear
      ? skipRuns
      : 0,
  );

  const mappedVehicleModels = mapVehicleModels({
    models: props.vehicleModels,
    brand,
    useBolttechData,
  });

  const mappedVehicleMakers = mapVehicleMakers(
    props.vehicleMakers?.filter((maker) => {
      if (typeof maker === 'string') {
        return !!maker;
      }
      if (typeof maker === 'object') {
        return !!(maker.id && maker.name);
      }
      return false;
    }) as string[] | { id: string; name: string }[],
    brand,
  );

  const mappedVehicleTrims =
    props?.trims && props?.trims.length > 0
      ? mapVehicleTrims(props?.trims as VehicleTrim[])
      : [];

  const { yearsOptions: defaultYearsOptions } = config({
    useBolttechData,
  })[brand];
  const [yearsOptions] = useState(defaultYearsOptions);
  const [driverValue, setDriverValue] = useState<Driver>(initialState.driver);
  const [vehicleValue, setVehicleValue] = useState<Vehicle>(
    initialState.vehicle,
  );
  const [pricesValue, setPricesValue] = useState(
    initialState?.prices || {
      propertyDamage: 0,
      personalAccident: 0,
      isActsOfNature: false,
    },
  );
  const [user, setUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [isVehiclesLoading, setIsVehiclesLoading] = useState<boolean>(false);
  const [isYearsLoading] = useState<boolean>(false);
  const [isTrimsLoading, setIsTrimsLoading] = useState<boolean>(false);

  const [ncdValue, setNcdValue] = useState<number>(initialState.ncd);
  const [offenceFreeValue, setOffenceFreeValue] = useState<string>(
    initialState?.offenceFree || '',
  );
  const [offPeakVehicleValue, setOffPeakVehicleValue] = useState<string>(
    initialState?.offPeakVehicle || '',
  );
  const [providers, setProviders] = useState<string[]>([]);
  const [sort, setSort] = useState<SortType>(
    isMoneyMax ? 'Price' : 'Recommended',
  );

  const [isEditQuoteSidebarOpen, setIsEditQuoteSidebarOpen] =
    useState<boolean>(false);

  const [isProvidersSidebarOpen, setIsProvidersSidebarOpen] =
    useState<boolean>(false);
  const [isSortSidebarOpen, setIsSortSidebarOpen] = useState<boolean>(false);
  const [isQuoteDetailsDialogOpen, setIsQuoteDetailsOpen] = useState(false);

  const [vehicleMakers, setVehicleMakers] =
    useState<{ value: string; label: string }[]>(mappedVehicleMakers);
  const [vehicleModels, setVehicleModels] =
    useState<{ value: string; label: string }[]>(mappedVehicleModels);
  const [vehicleTrims, setVehicleTrims] =
    useState<{ value: string; label: string }[]>(mappedVehicleTrims);

  const [shouldOverride, setShouldOverride] = useState<boolean>(false);

  const fetchVehicleModels = async () => {
    if (useBolttechData) {
      setVehicleValue((vehicleValue) => ({
        ...vehicleValue,
        modelCode: '',
        modelName: '',
        trim: '',
      }));
    }

    setIsVehiclesLoading(true);

    const vehicles = await vehicleClient.getVehiclesData({
      makeName: vehicleValue.makeName,
      useBolttechData,
    });

    const mappedValues = mapVehicleModels({
      models: vehicles,
      brand,
      useBolttechData,
    });
    setVehicleModels(mappedValues);
    if (shouldOverride) {
      setVehicleValue((vehicleValue) => ({
        ...vehicleValue,
        modelCode:
          isMoneyMax || props?.useBolttechData ? '' : mappedValues?.[0]?.value,
        modelName:
          isMoneyMax || props?.useBolttechData ? '' : mappedValues?.[0]?.label,
        fairMarket: 0,
        trim: isSingSaver ? '' : vehicleValue.trim,
      }));
      setVehicleTrims([]);
    } else {
      setVehicleValue((vehicleValue) => ({
        ...vehicleValue,
        modelCode: isMoneyMax ? vehicleValue.modelCode : mappedValues[0]?.value,
        modelName: isMoneyMax ? vehicleValue.modelName : mappedValues[0]?.label,
        fairMarket: vehicleValue.fairMarket,
      }));
    }
    setIsVehiclesLoading(false);
  };

  // Fetch models when Brand changes
  useEffect(() => {
    if (skipInitialFetchModelEffect.current > 0) {
      skipInitialFetchModelEffect.current -= 1;
      return;
    }

    if (vehicleValue.makeName) {
      fetchVehicleModels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    vehicleValue.makeName,
    initialState.vehicle.makeName,
    brand,
    useBolttechData,
    vehicleClient,
  ]);

  // ToDo: uncomment after form stabilize
  // Fetch years when Brand or Model changes / Moneymax only
  // useEffect(() => {
  //   if (!isMoneyMax) {
  //     return;
  //   }

  //   if (
  //     vehicleValue.modelCode &&
  //     vehicleValue.modelCode !== initialState.vehicle.modelCode
  //   ) {
  //     setIsYearsLoading(true);
  //     vehicleClient
  //       .getModelYears(vehicleValue.makeName, vehicleValue.modelCode)
  //       .then((yearsOptions) => {
  //         if (yearsOptions.length === 0) {
  //           return;
  //         }

  //         setYearsOptions(mapNumbersToLabelValue(yearsOptions));
  //       })
  //       .finally(() => {
  //         setIsYearsLoading(false);
  //       });
  //   }
  // }, [
  //   vehicleValue.makeName,
  //   vehicleValue.modelCode,
  //   initialState.vehicle.modelCode,
  //   isMoneyMax,
  //   vehicleClient,
  // ]);

  // Fetch trims when Brand or Model changes / SingSaver only + Bolttech
  useEffect(() => {
    if (skipInitialFetchTrimEffect.current > 0) {
      skipInitialFetchTrimEffect.current -= 1;
      return;
    }

    if (!isSingSaver || !useBolttechData) {
      return;
    }

    if (!vehicleValue.makeName || !vehicleValue.modelCode) {
      return;
    }

    setVehicleValue((vehicleValue) => ({
      ...vehicleValue,
      trim: '',
    }));
    setIsTrimsLoading(true);

    vehicleClient
      .getVehicleTrims({
        makeId: vehicleValue.makeName,
        modelId: vehicleValue.modelCode,
        year: null,
      })
      .then((vehicleTrims) => {
        if (!vehicleTrims || vehicleTrims.length === 0) {
          return;
        }

        const mappedVehicleTrims = mapVehicleTrims(vehicleTrims);
        setVehicleTrims(mappedVehicleTrims);
      })
      .finally(() => {
        setIsTrimsLoading(false);
      });
  }, [
    isSingSaver,
    useBolttechData,
    vehicleClient,
    initialState.vehicle.makeName,
    initialState.vehicle.modelCode,
    vehicleValue.makeName,
    vehicleValue.modelCode,
  ]);

  // Fetch trims when Year changes / Moneymax only
  useEffect(() => {
    if (!isMoneyMax) {
      return;
    }

    // Variants belong to the chosen make/model/year: clear the previous car's list first (CMS-2047).
    setVehicleTrims([]);
    setIsTrimsLoading(false);

    if (
      !vehicleValue.makeName ||
      !vehicleValue.modelCode ||
      !vehicleValue.manufactureYear
    ) {
      return;
    }

    // A late response for a previous make/model/year must not fill the list.
    let isStale = false;

    setIsTrimsLoading(true);
    vehicleClient
      .getVehicleTrims({
        makeId: vehicleValue.makeName,
        modelId: vehicleValue.modelCode,
        year: +vehicleValue.manufactureYear,
      })
      .then((vehicleTrims) => {
        if (isStale) {
          return;
        }

        const mappedVehicleTrims = mapVehicleTrims(vehicleTrims ?? []);
        setVehicleTrims(mappedVehicleTrims);
      })
      .finally(() => {
        if (!isStale) {
          setIsTrimsLoading(false);
        }
      });

    return () => {
      isStale = true;
    };
  }, [
    isMoneyMax,
    vehicleValue.makeName,
    vehicleValue.modelCode,
    vehicleValue.manufactureYear,
    initialState.vehicle.manufactureYear,
    vehicleClient,
  ]);

  // Fair market value fetch on vehicle value change / Moneymax only
  useEffect(() => {
    if (!isMoneyMax) {
      return;
    }

    if (
      !vehicleValue.makeName ||
      !vehicleValue.modelCode ||
      !vehicleValue.manufactureYear ||
      !vehicleValue.trim
    ) {
      return;
    }

    fairMarketClient
      .getVehicleFairMarketValue(
        vehicleValue.makeName,
        vehicleValue.modelCode,
        +vehicleValue.manufactureYear,
        vehicleValue.trim,
      )
      .then((fairMarket) => {
        setVehicleValue((vehicleValue) => ({
          ...vehicleValue,
          fairMarket: fairMarket.fmv,
        }));
      });
  }, [
    isMoneyMax,
    vehicleValue.makeName,
    vehicleValue.modelCode,
    vehicleValue.manufactureYear,
    vehicleValue.trim,
    setVehicleValue,
    fairMarketClient,
  ]);

  // Reset Vehicle when Year changes and shouldOverride is true / Moneymax only
  useEffect(() => {
    if (!isMoneyMax || !shouldOverride) {
      return;
    }

    setVehicleValue((vehicleValue) => ({
      ...vehicleValue,
      trim: '',
      fairMarket: 0,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMoneyMax, vehicleValue.manufactureYear]);

  // Reset Vehicle when Model changes and shouldOverride is true / Moneymax only
  useEffect(() => {
    if (!isMoneyMax || !shouldOverride) {
      return;
    }

    setVehicleValue((vehicleValue) => ({
      ...vehicleValue,
      manufactureYear: '',
      trim: '',
      fairMarket: 0,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleValue.modelCode, isMoneyMax]);

  // ---- Handlers ----
  const handleDriverValueChange = useMemo(
    () => (updates: Omit<Field<Driver>, 'dateOfBirth'>) => {
      const dateOfBirth = updates.age
        ? getDateOfBirth(updates.age)
        : driverValue.dateOfBirth;
      setDriverValue({ ...driverValue, ...updates, dateOfBirth });
    },
    [driverValue],
  );

  const handleVehicleValueChange = useMemo(
    () => (updates: Field<Vehicle>) => {
      setVehicleValue((x) => ({ ...x, ...updates }));
    },
    [setVehicleValue],
  );

  const handlePricesValueChange = useMemo(
    () => (updates: Field<Prices>) => {
      setPricesValue({ ...pricesValue, ...updates });
    },
    [pricesValue],
  );

  const handleNcdValueChange = (value: number) => {
    setNcdValue(value);
  };

  const handleOffenceFreeChange = (value: string) => {
    setOffenceFreeValue(value);
  };

  const handleOffPeakVehicleChange = (value: string) => {
    setOffPeakVehicleValue(value);
  };

  const handleToggleProvider = useCallback(
    (value: string) => {
      const isActive = providers.includes(value);
      if (isActive) {
        const newState = providers.filter((provider) => provider !== value);
        setProviders(newState);
      } else {
        const newState = providers.concat([value]);
        setProviders(newState);
      }
    },
    [providers],
  );

  const handleProvidersChange = (value: string[]) => {
    setProviders(value);
  };

  const handleSortChange = (value: SortType) => {
    setSort(value);
  };

  const handleToggleEditQuoteSidebar = (value: boolean) => {
    setIsEditQuoteSidebarOpen(value);
  };

  const handleToggleProvidersSidebar = (value: boolean) => {
    setIsProvidersSidebarOpen(value);
  };

  const handleToggleSortSidebar = (value: boolean) => {
    setIsSortSidebarOpen(value);
  };

  const handleVehicleMakersChange = (
    value: { value: string; label: string }[],
  ) => {
    setVehicleMakers(value);
  };

  const handleVehicleModelsChange = (
    value: { value: string; label: string }[],
  ) => {
    setVehicleModels(value);
  };

  const handleShouldOverrideChange = (value: boolean) => {
    setShouldOverride(value);
  };

  const sendLeadToZoho = SendLeadToZoho;

  const value: CarInsuranceContextState = useMemo(
    () => ({
      options: {
        shouldOverride: {
          setValue: handleShouldOverrideChange,
          value: shouldOverride,
        },
      },
      form: {
        isChanged: isFormChanged,
        setIsChanged: setIsFormChanged,
      },
      requestParams: {
        driver: {
          value: driverValue,
          setValue: handleDriverValueChange,
        },
        vehicle: {
          value: vehicleValue,
          setValue: handleVehicleValueChange,
        },
        ncd: {
          value: ncdValue,
          setValue: handleNcdValueChange,
        },
        offenceFree: {
          value: offenceFreeValue,
          setValue: handleOffenceFreeChange,
        },
        offPeakVehicle: {
          value: offPeakVehicleValue,
          setValue: handleOffPeakVehicleChange,
        },
        prices: {
          value: pricesValue,
          setValue: handlePricesValueChange,
        },
        user: {
          value: user,
          setValue: setUser,
        },
      },
      filters: {
        providers: {
          value: providers,
          setValue: handleProvidersChange,
          toggleProvider: handleToggleProvider,
        },
        sort: {
          value: sort,
          setValue: handleSortChange,
        },
      },
      sidebars: {
        editQuoteSidebar: {
          isOpen: isEditQuoteSidebarOpen,
          setIsOpen: handleToggleEditQuoteSidebar,
        },
        providersSidebar: {
          isOpen: isProvidersSidebarOpen,
          setIsOpen: handleToggleProvidersSidebar,
        },
        sortSidebar: {
          isOpen: isSortSidebarOpen,
          setIsOpen: handleToggleSortSidebar,
        },
      },
      dialogs: {
        quoteDetailsDialog: {
          isOpen: isQuoteDetailsDialogOpen,
          setIsOpen: setIsQuoteDetailsOpen,
        },
      },
      data: {
        vehicleMakers: {
          value: vehicleMakers,
          setValue: handleVehicleMakersChange,
        },
        vehicleModels: {
          value: vehicleModels,
          setValue: handleVehicleModelsChange,
        },
        vehicleTrims: { value: vehicleTrims },
        isVehiclesDataLoading: isVehiclesLoading,
        isYearsLoading: isYearsLoading,
        isTrimsLoading: isTrimsLoading,
        yearsOptions: { value: yearsOptions },
      },
      useBolttechData,
      sendLeadToZoho,
      getCarInsuranceLayoutId,
    }),
    [
      driverValue,
      handleDriverValueChange,
      handlePricesValueChange,
      handleToggleProvider,
      handleVehicleValueChange,
      isEditQuoteSidebarOpen,
      isFormChanged,
      isProvidersSidebarOpen,
      isQuoteDetailsDialogOpen,
      isSortSidebarOpen,
      isTrimsLoading,
      isVehiclesLoading,
      isYearsLoading,
      ncdValue,
      offPeakVehicleValue,
      offenceFreeValue,
      pricesValue,
      providers,
      shouldOverride,
      sort,
      useBolttechData,
      user,
      vehicleMakers,
      vehicleModels,
      vehicleTrims,
      vehicleValue,
      yearsOptions,
      sendLeadToZoho,
    ],
  );
  return (
    <CarInsuranceContext.Provider value={value}>
      {props.children}
    </CarInsuranceContext.Provider>
  );
}
